import { axios } from '@/plugins/axios';
import { Resource as FileResource } from '@/modules/core/files/services/StorageService';
import { Pagination, Resource, KeyValue } from '@/helpers/Interfaces';
import Pager from '@/helpers/Pager';
import { DateTime } from 'luxon';

/**
 * AssistantService
 */
export default class AssistantService
{
    public static path = 'admin/elearning/ai/';

    /**
     * @returns Promise<void>
     */
    public static async CreateAssistantAsync(model: AssistantItemModel): Promise<AssistantItemModel>
    {
        return (await axios.post<AssistantItemModel>('admin/ai/tools/assistant/createassistant', model)).data;
    }

    /**
     * @returns Promise<void>
     */
    public static async UpdateAssistantAsync(model: AssistantItemModel): Promise<AssistantItemModel>
    {
        return (await axios.post<AssistantItemModel>('admin/ai/tools/assistant/updateassistant', model)).data;
    }

    /**
     * @returns Promise<AssistantListItemModel>
     */
    public static async GetAssistantsAsync(): Promise<AssistantListItemModel[]>
    {
        return (await axios.get<AssistantListItemModel[]>('admin/ai/tools/assistant/getassistants')).data;
    }

    /**
     * @returns Promise<AssistantItemModel>
     */
    public static async GetAssistantAsync(id: string): Promise<AssistantItemModel>
    {
        return (await axios.get<AssistantItemModel>(`admin/ai/tools/assistant/getassistant/${id}`)).data;
    }

    /**
     * @returns Promise<boolean>
     */
    public static async DeleteAssistantAsync(id: string): Promise<boolean>
    {
        return (await axios.get<boolean>(`admin/ai/tools/assistant/deleteassistant/${id}`)).data;
    }

    /**
     * @returns Promise<AssistantFilesListItemModel>
     */
    public static async GetAssistantFilesAsync(): Promise<AssistantFilesListItemModel[]>
    {
        return (await axios.get<AssistantFilesListItemModel[]>('admin/ai/tools/assistant/getfiles')).data;
    }

    /**
     * @returns Promise<void>
     */
    public static async UploadAssistantFileAsync(model: AssistantFileItemModel): Promise<void>
    {
        const fileData = new FormData();

        fileData.append('file', model.file.upload);

        return (await axios.post('admin/ai/tools/assistant/uploadfile', fileData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            params: {
                filename: model.filename
            }
        }
        )).data;
    }

    /**
     * @returns Promise<void>
     */
    public static async UploadFileRawRequestAsync(model: UploadFileRawRequestModel): Promise<any>
    {
        const fileData = new FormData();

        fileData.append('file', model.file);

        return (await axios.post('admin/ai/tools/assistant/uploadfile', fileData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        }
        )).data;
    }

    /**
     * @returns Promise<void>
     */
    public static async UploadFileFromPickerAsync(file: File): Promise<any>
    {
        return (await axios.post('admin/ai/tools/assistant/uploadfile', file)).data;
    }

    /**
     * @returns Promise<any>
     */
    public static async DeleteAssistantFileAsync(id: string): Promise<void>
    {
        return (await axios.get(`admin/ai/tools/assistant/deletefile/${id}`)).data;
    }

    /**
     * @returns Promise<void>
     */
    public static async CreateAssistantMessageAsync(model: AssistantRequestDataModel): Promise<AssistantRunItemModel>
    {
        return (await axios.post<AssistantRunItemModel>('admin/ai/tools/assistant/createmessage', model)).data;
    }

    /**
     * @returns Promise<AssistantRunItemModel[]>
     */
    // public static async AssistantRunAsync(model: AssistantRequestDataModel): Promise<AssistantRunItemModel[]>
    // {
    //     return (await axios.post<AssistantRunItemModel[]>('admin/ai/tools/assistant/runassistant', model)).data;
    // }
    public static async AssistantRunAsync(model: AssistantRequestDataModel): Promise<any>
    {
        return (await axios.post<any>('admin/ai/tools/assistant/runassistant', model)).data;
    }
    public static async AssistantRunStreamAsync(model: AssistantRequestDataModel): Promise<any>
    {
        return (await axios.post<any>('admin/ai/tools/assistant/run-assistant-stream', model, { responseType: 'stream' })).data;
    }

    /**
     * @returns Promise<void>
     */
    public static async GetAssistantMessagesAsync(threadId: string): Promise<any[]>
    {
        return (await axios.get<any[]>(`admin/ai/tools/assistant/getmessages/${threadId}`)).data;
    }

    /**
     * @returns Promise<AssistantThreadModel>
     */
    public static async CreateAssistantThreadAsync(model: AssistantThreadRequestModel): Promise<AssistantThreadModel>
    {
        return (await axios.post<AssistantThreadModel>('admin/ai/tools/assistant/createthread', model)).data;
    }

    /**
     * @returns Promise<AssistantThreadModel>
     */
    public static async UpdateAssistantThreadAsync(model: AssistantThreadRequestModel): Promise<boolean>
    {
        return (await axios.post<boolean>('admin/ai/tools/assistant/updatethread', model)).data;
    }

    /**
     * @returns Promise<AssistantThreadModel>
     */
    public static async DeleteAssistantThreadAsync(threadId: string): Promise<boolean>
    {
        return (await axios.delete<boolean>(`admin/ai/tools/assistant/deletethread/${threadId}`)).data;
    }

    /**
     * @returns Promise<AssistantThreadModel[]>
     */
    public static async GetAssistantThreadsAsync(): Promise<AssistantThreadModel[]>
    {
        return (await axios.get<AssistantThreadModel[]>('admin/ai/tools/assistant/getthreads')).data;
    }

    /**
     * @returns Promise<AssistantFunctionFormModel>
     */
    public static async GetAssistantFunctionAsync(id: number): Promise<Resource<AssistantFunctionFormModel>>
    {
        return (await axios.get<Resource<AssistantFunctionFormModel>>(`admin/ai/tools/assistant/getfunction/${id}`)).data;
    }

    /**
     * @returns Promise<Resource<AssistantFunctionFormModel>>
     */
    public static async CreateAssistantFunctionAsync(model: AssistantFunctionFormModel): Promise<Resource<AssistantFunctionFormModel>>
    {
        return (await axios.post<Resource<AssistantFunctionFormModel>>('admin/ai/tools/assistant/createfunction', model)).data;
    }

    /**
     * @returns Promise<Resource<AssistantFunctionFormModel>>
     */
    public static async UpdateAssistantFunctionAsync(id: number, model: AssistantFunctionFormModel): Promise<Resource<AssistantFunctionFormModel>>
    {
        return (await axios.put<Resource<AssistantFunctionFormModel>>(`admin/ai/tools/assistant/updatefunction/${id}`, model)).data;
    }

    /**
     * @returns Promise<Pagination<AssistantFunctionListItemModel>>
     */
    public static async GetAssistantFunctionsAsync(pager: Pager): Promise<Pagination<AssistantFunctionListItemModel>>
    {
        return (await axios.get<Pagination<AssistantFunctionListItemModel>>(`admin/ai/tools/assistant/getfunctions`, {
            params: {...pager.data()}
        })).data;
    }

    /**
     * @returns Promise<Pagination<AssistantFunctionListItemModel>>
     */
    public static async GetAssistantFunctionOptionsAsync(): Promise<AssistantFunctionListItemModel[]>
    {
        return (await axios.get<AssistantFunctionListItemModel[]>('admin/ai/tools/assistant/getfunctionoptions')).data;
    }

    /**
     * @returns Promise<Statement>
     */
    public static async DeleteAssistantFunctionAsync(id: number): Promise<Statement>
    {
        return (await axios.delete(`admin/ai/tools/assistant/deletefunction/${id}`)).data as Statement;
    }

    /**
     * @returns Promise<any>
     */
    public static async getFileContent(fileId: string, filename: string): Promise<any>
    {
        return axios
            .post(`admin/ai/tools/assistant/get-file-content/${fileId}`, {
                responseType: 'blob'
            })
            .then(res =>
            {
                if (res.status == 200)
                {
                    const blob = res.data;

                    if ((window.navigator as any).msSaveOrOpenBlob)
                    {
                        (navigator as any).msSaveBlob(blob, filename);
                    }
                    else
                    {
                        const a = document.createElement('a');

                        document.body.appendChild(a);
                        a.href = window.URL.createObjectURL(blob);
                        a.download = filename;
                        a.target = '_blank';
                        a.click();
                        a.remove();
                    }
                }
            });
    }

    public static async AttachFilesToAssistant(model: AttachFilesToAssistantRequest): Promise<boolean>
    {
        return (await axios.post(this.path + 'tools/assistant/attach-files', model)).data;
    }
}

export interface AssistantListItemModel
{
    id: string;
    name: string;
    model: string;
    utcDateTime: DateTime;
    metadata: Record<string, string>;
}

export interface AssistantItemModel
{
    id: string;
    utcDateTime: DateTime;
    name: string;
    model: string;
    instructions: string;
    tools: string[];
    fileIds: string[];
    functions: string[];
    metadata: Record<string, string>;
}

export interface AssistantFilesListItemModel
{
    id: string;
    filename: string;
    utcDateTime: DateTime;
}

export interface UploadFileRawRequestModel
{
    file: File;
}

export interface AssistantFileItemModel
{
    filename: string,
    file: FileResource
}

export interface FileFromPicker
{
    file?: File;
}

export interface AssistantRunItemModel
{
    id: string,
    createdAt: string,
    threadId: string,
    role: any,
    contentItems: string[],
    assistantId: string,
    runId: string,
    fileIds: string[],
    metadata: any
}

export interface AssistantChatItemModel
{
    id: string,
    utcDateTime: DateTime,
    role: string,
    content: any[],
}

export interface ContentItemModel
{
    text: string,
    type: string
}

export interface AssistantRequestDataModel
{
    threadId: string,
    assistantId: string,
    messageContent: string
}

// AssistantThread
export interface AssistantThreadModel
{
    threadId: string;
    threadName: string;
    isMainThread: boolean;
    createdAt?: DateTime;
}

export interface AssistantThreadRequestModel
{
    threadId: string;
    threadName: string;
    isMainThread: boolean;
}

export interface AssistantThreadResponseModel
{
    threadId: string;
    threadName: string;
    isMainThread: boolean;
    createdAt?: DateTime;
}
// AssistantThread

export interface AssistantFunctionFormModel
{
    id: number;
    name: string;
    description: string;
    parameters: string;
}

export interface AssistantFunctionListItemModel
{
    id: number;
    name: string;
    description: string;
}

export interface Statement
{
    code?: number;
    message?: string;
}

export interface AttachFilesToAssistantRequest
{
    assistantId: string;
    fileIds: string[];
}
